import { useEffect, useCallback } from 'react';

/**
 * Hook that adds anti-screenshot/copy protections to a page.
 * - Blocks PrintScreen, Ctrl+P, Ctrl+S, Ctrl+Shift+S
 * - Blurs content on tab/window focus loss
 * - Disables right-click on protected areas
 * - Disables print via CSS
 */
export function useContentProtection(containerRef, { enabled = true } = {}) {
  const blurContent = useCallback(() => {
    if (!enabled || !containerRef?.current) return;
    containerRef.current.style.filter = 'blur(20px)';
    containerRef.current.style.transition = 'filter 0.1s';
  }, [enabled, containerRef]);

  const unblurContent = useCallback(() => {
    if (!containerRef?.current) return;
    containerRef.current.style.filter = 'none';
  }, [containerRef]);

  useEffect(() => {
    if (!enabled) return;

    // Block keyboard shortcuts
    const handleKeyDown = (e) => {
      // PrintScreen
      if (e.key === 'PrintScreen') {
        e.preventDefault();
        blurContent();
        setTimeout(unblurContent, 1500);
        return false;
      }
      // Ctrl+P (print), Ctrl+S (save), Ctrl+Shift+S (screenshot)
      if (e.ctrlKey && (e.key === 'p' || e.key === 's' || e.key === 'P' || e.key === 'S')) {
        e.preventDefault();
        return false;
      }
      // Cmd+Shift+3/4/5 (macOS screenshots)
      if (e.metaKey && e.shiftKey && ['3', '4', '5'].includes(e.key)) {
        e.preventDefault();
        blurContent();
        setTimeout(unblurContent, 1500);
        return false;
      }
    };

    // Blur on visibility change (tab switch, OS screenshot overlay)
    const handleVisibility = () => {
      if (document.hidden) {
        blurContent();
      } else {
        // Small delay to catch screenshot tools
        setTimeout(unblurContent, 300);
      }
    };

    // Blur on window blur (alt+tab, notification panel, etc.)
    const handleBlur = () => blurContent();
    const handleFocus = () => setTimeout(unblurContent, 200);

    // Block right-click
    const handleContext = (e) => {
      if (containerRef?.current?.contains(e.target)) {
        e.preventDefault();
        return false;
      }
    };

    document.addEventListener('keydown', handleKeyDown, { capture: true });
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('blur', handleBlur);
    window.addEventListener('focus', handleFocus);
    document.addEventListener('contextmenu', handleContext);

    // Inject print-blocking CSS
    const style = document.createElement('style');
    style.id = 'morphmx-print-protection';
    style.textContent = `@media print { body * { display: none !important; } body::after { content: "Contenido protegido - MorphMX"; display: block; font-size: 24px; text-align: center; padding: 100px; } }`;
    if (!document.getElementById('morphmx-print-protection')) {
      document.head.appendChild(style);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown, { capture: true });
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('contextmenu', handleContext);
      unblurContent();
    };
  }, [enabled, blurContent, unblurContent, containerRef]);
}
